import React, { forwardRef, useRef, useImperativeHandle, useEffect } from 'react';
import { createFilter } from 'react-select';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import bowser from 'yola-bowser';
import noop from '../../../utils/noop';
import hooks from './hooks';
import { GROUP_OPTION_TYPE, OPTION_TYPE } from './types';
import { SIZES, APPEARANCES, SEARCHABLE_APPEARANCES } from './constants';

const { useSelect, useInternalComponents, useSelectComponent } = hooks;

const filterConfig = {
  ignoreCase: true,
  ignoreAccents: true,
  trim: false,
  stringify: (option) => `${option.label}`,
  matchFrom: 'any',
};

const defaultComponents = {};
const defaultOptions = [];

const BaseSelect = forwardRef(
  (
    {
      appearance = APPEARANCES.default,
      clearable = true,
      components = defaultComponents,
      defaultInputValue = '',
      inputValue: controlledInputValue = null,
      disabled = false,
      id = null,
      invalid = false,
      name = null,
      noResultsText = 'No results found',
      onBlur = noop,
      onChange,
      onKeyDown = noop,
      onCreateOption = null,
      filterOptionsFunction = null,
      onFocus = noop,
      onInputChange = noop,
      placeholder = 'Select item',
      preventOverflow = false,
      size = SIZES.small,
      blurInputOnSelect = true,
      captureMenuScroll = false,
      options = defaultOptions,
      asyncConfig = null,
      // added for SimpleSelect
      value: controlledValue = null,
      defaultValue = null,
      className = null,
      transparent = false,
      iconComponent = null,
      prefixTextComponent = null,
      defaultMenuIsOpen = false,
      autoFocus = false,
      openMenuOnFocus = false,
      menuIsOpen = null,
      maxMenuHeight = null,
      menuWidth = '',
      shouldMarkSelectedOption = true,
      withDropdownIndicator = true,
      menuPlacement = 'auto',
      noVerticalSpacing = false,
      noHorizontalSpacing = false,
      noSpacingBetweenElements = false,
    },
    ref
  ) => {
    const isAutocomplete = appearance === APPEARANCES.autocomplete;
    const isSearchable = SEARCHABLE_APPEARANCES.includes(appearance);
    const isMobile = bowser.mobile;
    const isTablet = bowser.tablet;
    const isTouchDevice = isMobile || isTablet;
    const Select = useSelectComponent(isAutocomplete, asyncConfig);
    const selectRef = useRef(null);
    const timerRef = useRef(null);

    useImperativeHandle(
      ref,
      () => ({
        focus() {
          selectRef.current.focus();
        },
        selectOption(option) {
          selectRef.current.selectOption(option);
        },

        /*
         * TODO:
         * Update the solution when all browsers will
         * support the new virtual keyboard API on devices.
         * (Safari, Firefox do not support it now)
         */
        openMenuAutocomplete() {
          timerRef.current = setTimeout(() => {
            if (selectRef.current) {
              selectRef.current.onMenuOpen();
            }
          }, 150);
        },
      }),
      []
    );

    useEffect(() => () => clearTimeout(timerRef?.current), []);

    const {
      inputValue,
      isOpen,
      value,
      throttledHandleCloseOnScroll: handleCloseOnScroll,
      handleBlur,
      handleFocus,
      handleChange,
      handleClear,
      handleClose,
      handleInputChange,
      handleOpen,
    } = useSelect({
      defaultInputValue,
      controlledInputValue,
      controlledValue,
      isAutocomplete,
      isSearchable,
      onBlur,
      onFocus,
      onChange,
      onCreateOption,
      onInputChange,
      preventOverflow,
      defaultMenuIsOpen,
      openMenuOnFocus,
      menuIsOpen,
      withDropdownIndicator,
    });

    const customComponents = useInternalComponents(components, {
      withDropdownIndicator,
    });

    const classes = classNames('ws-ui-base-select', `ws-ui-base-select--${size}`, className, {
      'ws-ui-base-select--searchable': isSearchable,
      'ws-ui-base-select--is-disabled': disabled,
      'ws-ui-base-select--is-mobile': isMobile,
      'ws-ui-base-select--is-open': isOpen,
      'ws-ui-base-select--transparent': transparent,
      'ws-ui-base-select--with-menu-height-limit': maxMenuHeight,
      'ws-ui-base-select--no-vertical-spacing': noVerticalSpacing,
      'ws-ui-base-select--no-horizontal-spacing': noHorizontalSpacing,
      'ws-ui-base-select--no-spacing-between-elements': noSpacingBetweenElements,
    });

    const showCreateOptionMessage = isAutocomplete ? () => false : undefined;
    const noOptionMessage =
      (isAutocomplete || isSearchable) && !noResultsText ? null : noResultsText;
    const menuPortalTarget = preventOverflow ? document.body : null;

    const handleKeyDown = (event) => {
      onKeyDown(event, {
        focusedOption: selectRef.current.state.focusedOption,
      });
    };

    return (
      <Select
        ref={selectRef}
        styles={{
          menu: (styles) => ({
            ...styles,
            ...(menuWidth && { width: menuWidth }),
          }),
        }}
        blurInputOnSelect={blurInputOnSelect}
        className={classes}
        classNamePrefix="ws-ui-base-select"
        closeMenuOnScroll={handleCloseOnScroll}
        components={customComponents}
        defaultInputValue={defaultInputValue}
        filterOption={filterOptionsFunction || createFilter(filterConfig)}
        inputId={id}
        inputValue={isAutocomplete ? inputValue : undefined}
        invalid={invalid}
        isAutocomplete={isAutocomplete}
        isClearable={clearable}
        isDisabled={disabled}
        isOptionDisabled={(option) => option.disabled}
        isSearchable={isSearchable}
        isTouchDevice={isTouchDevice}
        isValidNewOption={showCreateOptionMessage}
        menuIsOpen={isOpen}
        menuPlacement={menuPlacement}
        menuPortalTarget={menuPortalTarget}
        menuShouldScrollIntoView={!preventOverflow}
        name={name}
        noOptionsMessage={() => noOptionMessage}
        onBlur={handleBlur}
        onChange={handleChange}
        onClear={handleClear}
        onFocus={handleFocus}
        onInputChange={handleInputChange}
        onMenuClose={handleClose}
        onMenuOpen={handleOpen}
        onKeyDown={handleKeyDown}
        options={options}
        placeholder={placeholder}
        size={size}
        value={controlledValue || value}
        captureMenuScroll={captureMenuScroll}
        {...asyncConfig}
        defaultValue={defaultValue}
        iconComponent={iconComponent}
        prefixTextComponent={prefixTextComponent}
        defaultMenuIsOpen={defaultMenuIsOpen}
        autoFocus={autoFocus}
        shouldMarkSelectedOption={shouldMarkSelectedOption}
        {...(maxMenuHeight && { maxMenuHeight })}
      />
    );
  }
);

BaseSelect.displayName = 'BaseSelect';

BaseSelect.propTypes = {
  appearance: PropTypes.oneOf(Object.values(APPEARANCES)),
  clearable: PropTypes.bool,
  components: PropTypes.shape({
    Option: PropTypes.func,
    SingleValue: PropTypes.func,
    Placeholder: PropTypes.func,
    Input: PropTypes.func,
    ValueContainer: PropTypes.func,
  }),
  menuPlacement: PropTypes.oneOf(['auto', 'bottom', 'top']),
  defaultInputValue: PropTypes.string,
  disabled: PropTypes.bool,
  blurInputOnSelect: PropTypes.bool,
  captureMenuScroll: PropTypes.bool,
  id: PropTypes.string,
  invalid: PropTypes.bool,
  preventOverflow: PropTypes.bool,
  name: PropTypes.string,
  noResultsText: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func,
  filterOptionsFunction: PropTypes.func,
  onFocus: PropTypes.func,
  inputValue: PropTypes.string,
  onInputChange: PropTypes.func,
  onCreateOption: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.shape(GROUP_OPTION_TYPE), PropTypes.shape(OPTION_TYPE)])
  ),
  asyncConfig: PropTypes.shape({
    loadOptions: PropTypes.func.isRequired,
    isLoading: PropTypes.bool,
    cacheOptions: PropTypes.oneOfType([PropTypes.shape(), PropTypes.bool]),
    defaultOptions: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.shape(GROUP_OPTION_TYPE), PropTypes.shape(OPTION_TYPE)])
      ),
    ]),
  }),
  placeholder: PropTypes.string,
  size: PropTypes.oneOf(Object.values(SIZES)),
  defaultValue: PropTypes.shape(OPTION_TYPE),
  className: PropTypes.string,
  transparent: PropTypes.bool,
  iconComponent: PropTypes.element,
  prefixTextComponent: PropTypes.element,
  defaultMenuIsOpen: PropTypes.bool,
  autoFocus: PropTypes.bool,
  openMenuOnFocus: PropTypes.bool,
  menuIsOpen: PropTypes.bool,
  maxMenuHeight: PropTypes.number,
  menuWidth: PropTypes.string,
  shouldMarkSelectedOption: PropTypes.bool,
  withDropdownIndicator: PropTypes.bool,
  value: PropTypes.shape(OPTION_TYPE),
  noVerticalSpacing: PropTypes.bool,
  noHorizontalSpacing: PropTypes.bool,
  noSpacingBetweenElements: PropTypes.bool,
};

export default BaseSelect;
